<template>
  <div ref="timeBlock"
       class="time-block"
       :class="classes"
       :style="styles"
       @click="onTimeBlockClick"
       @touchstart="onTouchStart"
       v-on-clickaway="hideResizeHandles">
    <div class="title">
      {{ modeTitle }}
    </div>
    <div v-if="$mq != 'mobile'"
         class="time">
      {{ timeFrom }} - {{ timeTo }}
    </div>

    <div class="delete-button"
         v-if="showDeleteButton && $mq != 'mobile'"
         @click="onDeleteClick">
      <icon-cross-bold width="10"
                       height="10"/>
    </div>

    <div v-show="modeName !== 'Standard' && showResizeHandles">
      <div class="resize-handle resize-handle-top resize-handle-line"></div>
      <div class="resize-handle resize-handle-top resize-handle-circle">
        <icon-arrow-down class="resize-handle-icon"
                         width="10"
                         height="10"/>
      </div>

      <div class="resize-handle resize-handle-bottom resize-handle-line"></div>
      <div class="resize-handle resize-handle-bottom resize-handle-circle">
        <icon-arrow-down class="resize-handle-icon"
                         width="10"
                         height="10"/>
      </div>
    </div>

    <div v-if="$mq === 'mobile'"
         class="mobile-delete-btn"
         ref="mobileDeleteBtn"
         @click="onDeleteClick">
      <div>{{ $t("schedule.removeTimeblock") }}</div>
    </div>
  </div>
</template>

<script>
  import interact from 'interactjs'
  import { convertMinsToHrsMins } from '@/services/utils.js'
  import { mixin as clickaway } from 'vue-clickaway'

  import tippy from 'tippy.js'

  export default {
    props: {
      height: {
        required: false,
        default: 20
      },

      top: {
        required: false,
        default: 0
      },

      bottom: {
        required: false,
        default: 0
      },

      modeName: {
        required: true,
        type: String
      },

      minBlockHeight: {
        required: true,
        type: Number
      },

      minBlockTime: {
        required: true,
        type: Number
      },

      parentBottom: {
        required: false,
        type: Number
      }
    },

    data() {
      return {
        showResizeHandles: false,
        timeBlockInitialZIndex: 0,
        blockIsMoving: false
      }
    },

    mounted() {
      this.initMobileRemoveBtn()
      let element = this.$refs.timeBlock

      this.timeBlockInitialZIndex = parseInt(window.getComputedStyle(this.$refs.timeBlock).zIndex)

      // Standard blocks are not interactable
      if (this.modeName === 'Standard') {
        return
      }

      interact(element)
        .resizable({
          edges: {
            top: '.resize-handle-top',
            bottom: '.resize-handle-bottom'
          },
          autoScroll: true,
          modifiers: [
            interact.modifiers.snap({
              targets: [
                interact.createSnapGrid({
                  x: 20, y: 20
                })
              ],
              offset: 'parent',
              range: Infinity,
              relativePoints: [ { x: 0, y: 0 } ]
            }),
            interact.modifiers.restrictEdges({
              outer: 'parent'
            }),
            interact.modifiers.restrictSize({
              min: { height: 20 },
            }),
          ],
        })
        .draggable({
          startAxis: 'y',
          lockAxis: 'y',
          autoScroll: true,

          modifiers: [
            interact.modifiers.snap({
              targets: [
                interact.createSnapGrid({
                  x: 20, y: 20
                })
              ],
              range: Infinity,
              offset: 'parent',
              relativePoints: [ { x: 0, y: 0 } ]
            }),
            interact.modifiers.restrict({
              restriction: 'parent',
              elementRect: { top: 0, left: 0, bottom: 1, right: 1 }
            })
          ],

          onmove: event => {
            event.dy = Math.round(event.dy) // Library "interact.js" doesn't round values when the page has zoom
            tippy.hideAll()

            this.$emit('move', event)
          },

          onstart: event => {
            this.blockIsMoving = true
            this.$emit('moveStart', event)
          },

          onend: event => {
            this.blockIsMoving = false
            this.$emit('moveEnd', event)
          }
        })
        .on('resizemove', event => {
          // Library "interact.js" doesn't round values when the page has zoom so we need to do it manually
          event.rect = this.getRoundedCoordinates(event.rect)
          event.deltaRect = this.getRoundedCoordinates(event.deltaRect)

          this.$emit('resize', event)
        })
        .on('resizestart', event => {
          this.$emit('resizeStart', event)
        })
        .on('resizeend', event => {
          this.$emit('resizeEnd', event)
        })
    },

    methods: {
      onTimeBlockClick(event) {
        if (this.$mq !== 'mobile') {
          this.toggleHandles()
          this.$emit('click', event)
        }
      },

      onTouchStart() {
        if (this.$mq === 'mobile') {
          this.toggleHandles()

          if (this.modeName !== 'Standard' && !this.blockIsMoving) {
            this.$refs.timeBlock._tippy.state.isShown
              ? this.$refs.timeBlock._tippy.hide()
              :this.$refs.timeBlock._tippy.show()
          }
        }
      },

      onDeleteClick() {
        tippy.hideAll()
        this.$emit('delete')
      },

      getRoundedCoordinates(coordinates) {
        let roundedCoordinates = {}

        Object.keys(coordinates).forEach(property => {
          roundedCoordinates[property] = Math.round(coordinates[property])
        })

        return roundedCoordinates
      },

      toggleHandles() {
        if (this.showResizeHandles) {
          this.hideResizeHandles()

          return
        }

        this.displayResizeHandles()
      },

      displayResizeHandles() {
        this.showResizeHandles = true

        this.$refs.timeBlock.style.zIndex = `${this.timeBlockInitialZIndex + 1}`
      },

      hideResizeHandles() {
        this.showResizeHandles = false

        this.$refs.timeBlock.style.zIndex = `${this.timeBlockInitialZIndex}`
      },

      initMobileRemoveBtn() {
        if (this.$mq === 'mobile') {
          tippy(this.$refs.timeBlock,  {
            allowHTML: true,
            content: this.$refs.mobileDeleteBtn,
            trigger: 'manual',
            hideOnClick: true,
            interactive: true,
            theme: 'custom-light',
            placement: 'top',
            animation: 'shift-away',
            arrow: true,
            inertia: true,
            duration: [400, 300]
          })
        }
      }
    },

    computed: {
      styles() {
        return {
          height: this.height ? this.height + 'px' : null,
          transform: 'translateY(' + this.top + 'px)'
        }
      },

      classes() {
        return {
          'mode-no-internet':   this.modeName === 'No Internet',
          'mode-device-paused': this.modeName === 'Pause Device',
          'mode-standard':       this.modeName === 'Standard',
          'one-line':           this.height === this.minBlockHeight,
          'time-block-mobile': this.$mq === 'mobile'
        }
      },

      timeFrom() {
        let mins = this.top * (this.minBlockTime / this.minBlockHeight)

        return convertMinsToHrsMins(mins)
      },

      timeTo() {
        let mins = (this.top + this.height) * (this.minBlockTime / this.minBlockHeight)

        return convertMinsToHrsMins(mins)
      },

      modeTitle() {
        switch (this.modeName) {
          case 'No Internet': return 'No Internet'
          case 'Pause Device': return 'Device Paused'
          default: return 'Standard'
        }
      },

      showDeleteButton() {
        return this.modeName !== 'Standard'
      }
    },

    mixins: [ clickaway ]
  }
</script>

<style lang="scss" scoped>
  $z-indexes: (
    time-block: 1,
    resize-handle: 2,
    delete-button: 3
  );

  .time-block {
    display: flex;
    flex-direction: column;

    position: absolute;
    top: 0;
    left: 0;
    z-index: map.get($z-indexes, time-block);

    width: 100%;
    height: 100px;
    padding: 4px 8px;

    color: #fff;
    font-size: 20px;
    touch-action: none;

    box-sizing: border-box;
    user-select: none;

    &.one-line {
      flex-direction: row;
      align-items: center;
    }

    &.one-line .time {
      margin-left: 6px;
    }

    &.mode-standard {
      color: #787196;
    }

    &.mode-device-paused {
      background-color: #ec971d;

      .resize-handle-circle {
        background-color: #ec971d;
      }
    }

    &.mode-no-internet {
      background-color: map.get($colors, primary);

      .resize-handle-circle {
        background-color: map.get($colors, primary);
      }
    }

    .title {
      font-size: 12px;
      font-weight: bold;
    }

    .time {
      font-size: 10px;
    }

    .delete-button {
      position: absolute;
      top: 0;
      right: 0;
      z-index: map.get($z-indexes, delete-button);
      display: flex;
      padding: 5px;
      cursor: pointer;
    }

    $resize-handle-size: 16px;

    .resize-handle {
      position: absolute;
      z-index: map.get($z-indexes, resize-handle);
      touch-action: none;
      user-select: none;

      &.resize-handle-top {
        top: 0;
      }

      &.resize-handle-bottom {
        bottom: 0;
      }
    }

    .resize-handle-line {
      left: 0;
      right: 0;
      height: 10px;
    }

    .resize-handle-circle {
      left: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      width: $resize-handle-size;
      height: $resize-handle-size;
      color: #fff;
      border-radius: 50%;
      border: 1px solid #fff;

      &.resize-handle-top {
        transform: translateX(-50%) translateY(-50%);

        .resize-handle-icon {
          transform: rotateX(180deg);
        }
      }

      &.resize-handle-bottom {
        transform: translateX(-50%) translateY(50%);
      }
    }
  }

  .time-block-mobile {
    padding: 2px 2px;
    align-items: center;

    .title {
      font-size: 8px;
      text-align: center;
    }
  }

  .mobile-delete-btn {
    display: flex;
    justify-content: space-between;
    align-items: center;

    color: #999;
    padding: 10px;
  }
</style>
